import PropTypes from 'prop-types';
import React from 'react';
import FirebaseContext from './FirebaseContext';

import Config from '../config';

class MyFirebase {
  constructor(app) {
    this.instance = app;

    // Firebase Auth init
    this.auth = app.auth();
    this.auth.setPersistence(app.auth.Auth.Persistence.LOCAL);

    this.googleProvider = new app.auth.GoogleAuthProvider();
    this.facebookProvider = new app.auth.FacebookAuthProvider();

    // Firebase Firestore init
    this.db = app.firestore;
    const settings = {
      cacheSizeBytes: app.firestore.CACHE_SIZE_UNLIMITED,
    };
    this.db().settings(settings);
    /* this.db().enablePersistence({
      synchronizeTabs: true,
    }); */

    // Firebase Storage init
    this.storage = app.storage().ref();

    // Firebase Functions
    this.functions = app.functions();

    this.rtdb = app.database;
  }

  /* *********
   * Authentication
  ********** */
  doCreateUserWithEmailAndPassword = (email, password) => this.auth.createUserWithEmailAndPassword(email, password);

  doSignInWithEmailAndPassword = (email, password) => this.auth.signInWithEmailAndPassword(email, password);

  doSigninWithGoogle = () => this.auth.signInWithPopup(this.googleProvider);

  doSigninWithFacebook = () => this.auth.signInWithPopup(this.facebookProvider);

  doSignOut = () => this.auth.signOut();

  doPasswordUpdate = (password) => this.auth.currentUser.updatePassword(password);

  doVerifyPasswordResetCode = (actionCode) => this.auth.verifyPasswordResetCode(actionCode);

  doConfirmPasswordReset = (actionCode, newPassword) => this.auth.confirmPasswordReset(actionCode, newPassword);

  /* *********
   * Firestore (Database)
  ********** */
  user = (uid) => this.db().collection('users').doc(uid);

  users = () => this.db().collection('users');

  content = () => this.db().collection('content').doc('mentorshow');

  newsletter = () => this.db().collection('newsletter');

  comments = () => this.db().collection('comments');

  settings = (site) => this.db().collection('settings').doc(site);

  myTrasactions = (uid) => this.db().collection('transactions').where('userId', '==', uid).where('status', '==', 'SUCCESS').get();

  lessonRatings = (classId, lessonId, uid) => this.db().collection('ratings').doc(classId).collection('lessons').doc(lessonId).collection('byUser').doc(uid);

  classAllLessonsRatings = (classId) => this.db().collection('ratings').doc(classId).collection('lessons');

  classRatings = (classId, uid) => this.db().collection('ratings').doc(classId).collection('byUser').doc(uid);

  globalRating = () => this.db().collection('ratings').doc('global');

  /* *********
   * Functions
  ********** */
  createStripePaymentIntent = (args) => this.functions.httpsCallable('createStripePaymentIntent')(args);

  transferStripeFunds = (args) => this.functions.httpsCallable('transferStripFunds')(args);

  createStripeSubscription = (args) => this.functions.httpsCallable('createStripeSubscription')(args);

  cancelStripeSubscription = (args) => this.functions.httpsCallable('cancelStripeSubscription')(args);

  saveCardToken = (args) => this.functions.httpsCallable('saveCardToken')(args);

  createActiveCompaignContact = (args) => this.functions.httpsCallable('createActiveCompaignContact')(args);

  getRandomClassTeasers = (args) => this.functions.httpsCallable('getRandomClassTeasers')(args);

  getClassTeasers = (args) => this.functions.httpsCallable('getClassTeasers')(args);

  getAllStripeProductsAndPlans = (args) => this.functions.httpsCallable('getAllStripeProductsAndPlans')(args);

  sendPasswordResetEmail = (args) => this.functions.httpsCallable('sendPasswordResetEmail')(args);

  activatePass = (args) => this.functions.httpsCallable('activatePass')(args);

  activateClass = (args) => this.functions.httpsCallable('activateClass')(args);

  getPaymentIntentCharge = (args) => this.functions.httpsCallable('getPaymentIntentCharge')(args);

  sendVideoStats = (args) => this.functions.httpsCallable('sendVideoStats')(args);

  addNewComment = (args) => this.functions.httpsCallable('addNewComment')(args);

  applyPromoCode = (args) => this.functions.httpsCallable('applyPromoCode')(args);

  cancelPromoCode = (args) => this.functions.httpsCallable('cancelPromoCode')(args);

  createPaypalTransaction = (args) => this.functions.httpsCallable('createPaypalTransaction')(args);

  logBrowserVideoPlayConnection = (args) => this.functions.httpsCallable('logBrowserVideoPlayConnection')(args);

  logBrowserVideoPlayDisconnection = (args) => this.functions.httpsCallable('logBrowserVideoPlayDisconnection')(args);

  rateLesson = (args) => this.functions.httpsCallable('rateLesson')(args);

  rateClass = (args) => this.functions.httpsCallable('rateClass')(args);

  reviewClass = (args) => this.functions.httpsCallable('reviewClass')(args);

  addRemoveCommentLike = (args) => this.functions.httpsCallable('addRemoveCommentLike')(args);

  trackFbRegistrationComplete = (args) => this.functions.httpsCallable('trackFbRegistrationComplete')(args);

  trackFbPageView = (args) => this.functions.httpsCallable('trackFbPageView')(args);

  processPayment = (args) => this.functions.httpsCallable('processPayment')(args);

  referralInviteGuest = (args) => this.functions.httpsCallable('referralInviteGuest')(args);

  getReferrerName = (args) => this.functions.httpsCallable('getReferrerName')(args);

  sendBusinessContactForm = (args) => this.functions.httpsCallable('sendBusinessContactForm')(args);

  gift1year = (args) => this.functions.httpsCallable('gift1year')(args);

  get1yearSenderName = (args) => this.functions.httpsCallable('get1yearSenderName')(args);

  setMarketingAllocation = (args) => this.functions.httpsCallable('setMarketingAllocation')(args);

  /* **********
   * Utils
   *********** */

  getTime = () => this.db.FieldValue.serverTimestamp();
}

/**
 *
 * @param credentials
 * @param children
 * @returns {*}
 * @constructor
 */
const Firebase = ({ children }) => {
  const [firebase, setFirebase] = React.useState(null);

  React.useEffect(() => {
    if (!firebase && typeof window !== 'undefined') {
      const app = import('firebase/app');
      const auth = import('firebase/auth');
      const database = import('firebase/database');
      const firestore = import('firebase/firestore');
      const storage = import('firebase/storage');
      const functions = import('firebase/functions');

      Promise.all([
        app,
        auth,
        database,
        firestore,
        storage,
        functions,
      ]).then((values) => {

        const firebaseInstance = values[0].default;

        if (!firebaseInstance.apps.length) {
          firebaseInstance.initializeApp(Config.firebaseConfig);
        }

        const myFirebase = new MyFirebase(firebaseInstance);

        setFirebase(myFirebase);
      });
    }
  }, [firebase]);

  return (
    <FirebaseContext.Provider value={firebase}>
      {children}
    </FirebaseContext.Provider>
  );
};

Firebase.propTypes = {
  children: PropTypes.any,
};

Firebase.defaultProps = {
  children: null,
};

export default Firebase;
